// Resilient dynamic imports for the code-split routes and modals.
//
// A plain `lazy(() => import(...))` fails permanently on a single network
// blip: React.lazy caches the rejection, the error reaches the Sentry
// ErrorBoundary in index.tsx, and one missed chunk fetch white-screens the
// whole app. That is GlitchTip UI-APP-6 and UI-APP-7 (bingbot on /Login) and
// UI-APP-9 (an iPhone member on /OurSexplorationList). Every chunk URL in
// those events still returns 200, so all three were transient fetches rather
// than a stale deploy.
//
// The fix is to try again a couple of times before giving up. Reloading the
// page is held back for the one case a reload actually repairs: the chunk is
// genuinely gone because a deploy replaced it. A flaky or offline connection
// must never trigger a reload, because that throws away whatever the member
// was in the middle of and could loop.
import { lazy } from "react";
import type { ComponentType, LazyExoticComponent } from "react";

const MAX_ATTEMPTS = 3;

// Deliberately short: someone is watching a Suspense fallback while this runs.
const BACKOFF_MS = [300, 900];

const RELOAD_STAMP_KEY = "chunk-reload-at";
const RELOAD_COOLDOWN_MS = 10 * 60 * 1000;

type ModuleWithDefault<T> = { default: T };

const sleep = (ms: number) =>
  new Promise<void>((resolve) => setTimeout(resolve, ms));

// Chrome embeds the failing module URL in the message ("Failed to fetch
// dynamically imported module: https://..."). Safari ("Importing a module
// script failed.") and Firefox do not, so no URL is an ordinary case, not an
// anomaly, and everything below has to work without one.
function urlFromError(error: unknown): string | null {
  const message = error instanceof Error ? error.message : String(error ?? "");
  const match = message.match(/https?:\/\/[^\s"')]+/);
  return match ? match[0] : null;
}

function cacheBusted(url: string): string {
  return `${url}${url.includes("?") ? "&" : "?"}cb=${Date.now()}`;
}

// Held on an object so the tests can stub the direct-URL retry. A test cannot
// otherwise stop the real `import()` below from trying to resolve an absolute
// https URL through the module runner. Production always takes the real path.
export const chunkImporter = {
  fromUrl: (url: string) => import(/* @vite-ignore */ url),
};

// sessionStorage throws on touch in some embedded and private-mode web views.
// This helper only ever runs on an already-broken path, so it must never
// become the crash itself.
function readReloadStamp(): number {
  try {
    const raw = window.sessionStorage.getItem(RELOAD_STAMP_KEY);
    const parsed = raw === null ? Number.NaN : Number.parseInt(raw, 10);
    return Number.isFinite(parsed) ? parsed : 0;
  } catch {
    return 0;
  }
}

function writeReloadStamp(at: number): boolean {
  try {
    window.sessionStorage.setItem(RELOAD_STAMP_KEY, String(at));
    return true;
  } catch {
    return false;
  }
}

// Only a 404 or 410 says the chunk is really gone. A 200 means the app is fine
// and the fetch simply failed, any other status is someone else's problem
// (a proxy, a captive portal, a rate limit), and a probe that throws means the
// network is down. None of those are repaired by reloading.
async function chunkIsGone(url: string): Promise<boolean> {
  try {
    const response = await fetch(url, { method: "HEAD", cache: "no-store" });
    return response.status === 404 || response.status === 410;
  } catch {
    return false;
  }
}

async function shouldReload(failedUrl: string | null): Promise<boolean> {
  // Without a URL there is no way to tell a missing chunk from a dead network.
  if (!failedUrl) return false;

  // An offline device will fail the same way on the next load, so a reload
  // buys nothing and costs the member their page.
  if (typeof navigator !== "undefined" && navigator.onLine === false) {
    return false;
  }

  if (!(await chunkIsGone(failedUrl))) return false;

  const now = Date.now();
  if (now - readReloadStamp() < RELOAD_COOLDOWN_MS) return false;

  // Stamp first, and do not reload at all when the stamp cannot be written:
  // without it nothing bounds a reload loop, and a loop is worse than the
  // error boundary the caller falls back to.
  return writeReloadStamp(now);
}

/**
 * Run a dynamic import with a few retries, and reload the page only when the
 * chunk is provably gone. Exported separately from `lazyWithRetry` so the
 * policy can be tested directly, and so non-React callers can reuse it.
 */
export async function importWithRetry<T>(
  factory: () => Promise<ModuleWithDefault<T>>
): Promise<ModuleWithDefault<T>> {
  let lastError: unknown;
  let failedUrl: string | null = null;

  for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt += 1) {
    if (attempt > 0) {
      await sleep(BACKOFF_MS[attempt - 1] ?? BACKOFF_MS[BACKOFF_MS.length - 1]);
    }

    try {
      if (failedUrl) {
        // Re-import the exact URL with a cache-busting param, which gets past
        // a bad intermediate cache entry that the original specifier would
        // hit again. This yields a SEPARATE module instance from the one the
        // specifier would have produced. Acceptable here: every call site is a
        // leaf page or modal with no module-level state anyone else shares,
        // and it only happens on an already-failing path.
        return (await chunkImporter.fromUrl(
          cacheBusted(failedUrl)
        )) as ModuleWithDefault<T>;
      }
      return await factory();
    } catch (error) {
      lastError = error;
      // Keep the FIRST URL seen. It is the real chunk URL rather than a
      // cache-busted retry of it, so the HEAD probe asks about the right file.
      failedUrl = failedUrl ?? urlFromError(error);
    }
  }

  if (await shouldReload(failedUrl)) {
    window.location.reload();
    // Never resolves on purpose: React holds the Suspense fallback through the
    // navigation instead of flashing the error boundary on the way out.
    return new Promise<ModuleWithDefault<T>>(() => {});
  }

  throw lastError;
}

/**
 * Drop-in replacement for React.lazy that survives a transient chunk fetch
 * failure. Use it for every code-split component.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- mirrors React's own lazy() signature, which constrains on ComponentType<any>
export function lazyWithRetry<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>
): LazyExoticComponent<T> {
  return lazy(() => importWithRetry(factory));
}
